import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClipboard } from '../context/ClipboardContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, List, ListItem, ListItemText, TextField,
  Alert, IconButton, Divider, FormControlLabel, Switch, Backdrop,
  Autocomplete, Snackbar, CircularProgress, Tooltip, Badge
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save'; 
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; 
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// --- INTERFACES ---
interface Patient {
  id: string;
  name: string;
  lastName: string;
  patientId?: string;
  cedula?: string;
  idNumber?: string;
  birthDate?: string;
}

interface Study {
  id: string;
  name: string;
  description: string;
  commercialName?: string;
  dosage?: string;
  preparation?: string;
}

interface RecipeSide {
  studies: Study[];
  note: string;
  showData: boolean; 
}

interface Config {
  logoUrl?: string;
  headerImageUrl?: string;
  logoRecipeUrl?: string; 
  footerAddress?: string;
  favoriteSpecialties?: string; // 👈 AGREGA ESTA LÍNEA
}

interface StudyRequest {
  id: string;
  patientId: string;
  medications: Study[];
  note?: string;
  createdAt: string;
  type?: string;
}

function RecipeEstudiosPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clipboardItems, clearClipboard } = useClipboard();

  // --- ESTADOS ---
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isPatientSelectionRequired, setIsPatientSelectionRequired] = useState(false);

  const [allStudies, setAllStudies] = useState<Study[]>([]);
  
  const [activeSide, setActiveSide] = useState<'left' | 'right'>('left');
  const [leftSide, setLeftSide] = useState<RecipeSide>({ studies: [], note: '', showData: true });
  const [rightSide, setRightSide] = useState<RecipeSide>({ studies: [], note: '', showData: true });
  
  const [config, setConfig] = useState<Config>({});
  const [pastRequests, setPastRequests] = useState<StudyRequest[]>([]);
  
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSavingCloud, setIsSavingCloud] = useState(false); 
  const [includeSignature, setIncludeSignature] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [processStatus, setProcessStatus] = useState('');
  
  // Estado para el carrito manual
  const [openCartDialog, setOpenCartDialog] = useState(false);

  // ✅ Contar estudios en el carrito (solo study_request)
  const studiesInCart = Array.isArray(clipboardItems) 
      ? clipboardItems.filter((item: any) => item.type === 'study_request')
      : [];

  // --- UTILS DE CARGA DE IMÁGENES ---
  const processImageUrl = useCallback((url: string | undefined) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http')) return url;
    const cleanPath = url.startsWith('/') ? url.slice(1) : url;
    return `${API_URL}/${cleanPath}?t=${new Date().getTime()}`;
  }, []);

  // --- CARGA INICIAL ---
useEffect(() => {
    let mounted = true;
    const init = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }

      try {
        const headers: HeadersInit = { 'Authorization': `Bearer ${token}` };
        
        // 1. Cargamos Configuración y Pacientes primero
        const [configRes, patientsRes] = await Promise.all([
          fetch(`${API_URL}/api/config`, { headers }),
          fetch(`${API_URL}/api/patients`, { headers }),
        ]);

        let currentSpecs = '';
        if (configRes.ok) {
          const configData = await configRes.json();
          if (mounted) setConfig(configData);
          // Preparamos el filtro para la siguiente llamada
          if (configData.favoriteSpecialties) {
            currentSpecs = `&specs=${encodeURIComponent(configData.favoriteSpecialties)}`;
          }
        }

        if (mounted && patientsRes.ok) setAllPatients(await patientsRes.json());

        // 2. Cargamos los estudios YA FILTRADOS por las especialidades del médico
        const studiesRes = await fetch(`${API_URL}/api/studies?take=1000${currentSpecs}`, { headers });
        if (mounted && studiesRes.ok) setAllStudies(await studiesRes.json());

        if (mounted && !selectedPatient) setIsPatientSelectionRequired(true);
        
      } catch (error) {
        if (mounted) setError("Error al conectar con el servidor.");
      } finally {
        if (mounted) setIsInitialLoading(false);
      }
    };
    init();
    return () => { mounted = false; };
  }, [navigate, selectedPatient]);

  // ✅ PEGAR DESDE CARRITO (MANUAL)
  const handlePasteFromCart = () => {
    if (studiesInCart.length === 0) {
      setError("No hay estudios en el carrito.");
      return;
    }

    const newStudies: Study[] = studiesInCart.map((item: any) => {
      let fullDesc = item.description || item.dosage || '';
      if (item.preparation) fullDesc += (fullDesc ? ' | ' : '') + `PREP: ${item.preparation}`;
      return {
        id: String(item.id || Math.random()),
        name: item.name || item.commercialName || 'Estudio',
        description: fullDesc
      };
    });

    if (activeSide === 'left') {
      setLeftSide(prev => ({ ...prev, studies: [...prev.studies, ...newStudies] }));
    } else {
      setRightSide(prev => ({ ...prev, studies: [...prev.studies, ...newStudies] }));
    }
    
    const sideName = activeSide === 'left' ? 'IZQUIERDA' : 'DERECHA';
    setSuccess(`✅ ${newStudies.length} estudios agregados a columna ${sideName}.`);
    setOpenCartDialog(false);
    clearClipboard();
  };

  // --- BÚSQUEDA Y HISTORIAL ---
const searchStudies = useCallback(async (query: string) => {
    try { 
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const params = new URLSearchParams({ q: query, take: '1000' });
      
      // Si hay favoritas en el estado, las incluimos
      if (config.favoriteSpecialties) {
        params.append('specs', config.favoriteSpecialties);
      }
      
      const res = await fetch(`${API_URL}/api/studies?${params.toString()}`, { headers });

      if (res.ok) {
        setAllStudies(await res.json());
      }
    } catch (error) { 
      console.error("Error en búsqueda de estudios:", error); 
    }
  }, [config.favoriteSpecialties]);

  useEffect(() => {
    const timer = setTimeout(() => { if (searchTerm.length >= 2) searchStudies(searchTerm); }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, searchStudies]);

  const fetchPastRequests = useCallback(async (patientId: string) => {
    if (!patientId) return setPastRequests([]);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/recipes?patientId=${patientId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data: any[] = await res.json();
        setPastRequests(
          data.filter((r: any) => r.type === 'study_request').sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        );
      }
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { if (selectedPatient) fetchPastRequests(selectedPatient.id); }, [selectedPatient, fetchPastRequests]);

  // --- HANDLERS UI ---
  const handleAddStudy = (study: Study) => {
    const studyToAdd = { ...study, id: Math.random().toString(36).substr(2, 9) };
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, studies: [...prev.studies, studyToAdd] }));
    else setRightSide(prev => ({ ...prev, studies: [...prev.studies, studyToAdd] }));
  };

  const handleRemoveStudy = (studyId: string) => {
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, studies: prev.studies.filter(s => s.id !== studyId) }));
    else setRightSide(prev => ({ ...prev, studies: prev.studies.filter(s => s.id !== studyId) }));
  };

  const handleNoteChange = (text: string) => {
    if (activeSide === 'left') setLeftSide(prev => ({ ...prev, note: text }));
    else setRightSide(prev => ({ ...prev, note: text }));
  };

  const handleSaveRequest = async () => {
    const allStudiesToSave = [...leftSide.studies, ...rightSide.studies];
    if (!selectedPatient || allStudiesToSave.length === 0) { setError('No hay estudios.'); return; }
    
    try {
      await fetch(`${API_URL}/api/recipes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({
          patientId: selectedPatient.id, medications: allStudiesToSave, type: 'study_request',
          note: `[Izq]: ${leftSide.note} | [Der]: ${rightSide.note}`, createdBy: user?.id, doctorName: user?.name
        }),
      });
      await fetchPastRequests(selectedPatient.id);
      setSuccess('Historial actualizado.');
    } catch (error) { setError('Error al guardar.'); }
  };

  // --- GENERACIÓN PDF ---
  const generatePDFBlob = async () => {
    const printElement = document.getElementById('internal-print-view');
    if (!printElement) return null;

    const canvas = await html2canvas(printElement, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
    const imgData = canvas.toDataURL('image/jpeg', 0.98);

    const pdf = new jsPDF('l', 'mm', 'letter');
    pdf.addImage(imgData, 'JPEG', 0, 0, 279.4, 215.9);
    return pdf;
  };

  const handlePrint = async () => {
    if (leftSide.studies.length === 0 && rightSide.studies.length === 0) { setError("Nada que imprimir."); return; }
    setIsPrinting(true);
    setProcessStatus("Generando vista previa...");
    
    setTimeout(async () => {
        try {
            const pdf = await generatePDFBlob();
            if (pdf) {
                const blobUrl = URL.createObjectURL(pdf.output('blob'));
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = blobUrl;
                document.body.appendChild(iframe);
                iframe.onload = () => {
                    setTimeout(() => {
                        iframe.contentWindow?.print();
                        setIsPrinting(false);
                        setProcessStatus("");
                    }, 500);
                };
            } else { setIsPrinting(false); }
        } catch (e) {
            console.error(e);
            setIsPrinting(false);
            setError("Error al generar impresión.");
        }
    }, 2500); 
  };

  const handleArchiveToExpediente = async () => {
    if (!selectedPatient) { setError("Faltan datos."); return; }
    setIsPrinting(true); 
    setIsSavingCloud(true);
    setProcessStatus("Archivando...");

    setTimeout(async () => {
        try {
            const pdf = await generatePDFBlob();
            if (pdf) {
                const formData = new FormData();
                formData.append('file', pdf.output('blob'), `Estudios_${selectedPatient.lastName}.pdf`);
                formData.append('patientId', selectedPatient.id);
                formData.append('title', `Solicitud Estudios - ${new Date().toLocaleDateString()}`);
                formData.append('category', 'STUDY_REQUEST');

                const res = await fetch(`${API_URL}/api/documents`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                    body: formData
                });

                if (res.ok) setSuccess("✅ Archivado en Expediente");
                else throw new Error("Error subiendo");
            }
        } catch (e) { setError("Error al archivar."); } 
        finally {
            setIsSavingCloud(false);
            setIsPrinting(false);
            setProcessStatus("");
        }
    }, 2500);
  };

  if (isInitialLoading) return <Box sx={{display:'flex', justifyContent:'center', mt:10}}><CircularProgress /></Box>;

  // Selección inicial
  if (!selectedPatient || isPatientSelectionRequired) {
    return (
      <Dialog open={true} maxWidth="sm" fullWidth>
        <DialogTitle>Selección de Paciente</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={allPatients}
            getOptionLabel={(p) => `${p.name} ${p.lastName} (${p.cedula || 'S/C'})`}
            onChange={(_, v) => { if(v) { setSelectedPatient(v); setIsPatientSelectionRequired(false); } }}
            renderInput={(p) => <TextField {...p} label="Buscar Paciente" autoFocus />}
          />
        </DialogContent>
        <DialogActions><Button onClick={() => navigate('/dashboard')}>Salir</Button></DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }} open={isPrinting || isSavingCloud}>
        <CircularProgress color="inherit" />
        <Typography>{processStatus}</Typography>
      </Backdrop>

      {/* --- BARRA SUPERIOR UNIFICADA --- */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: 'white', borderBottom: '1px solid #ccc', p: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" color="error" startIcon={<ExitToAppIcon />} onClick={() => navigate('/dashboard')}>SALIR</Button>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', mx: 2 }}>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
                {selectedPatient.name} {selectedPatient.lastName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.9rem' }}>
                Cédula N°: {selectedPatient.cedula || selectedPatient.patientId || 'S/C'}
            </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Button variant="outlined" color="warning" onClick={() => { if(window.confirm('¿Reiniciar?')) { setLeftSide({studies:[],note:'',showData:true}); setRightSide({studies:[],note:'',showData:true}); } }}>Reiniciar</Button>

        <FormControlLabel control={<Switch checked={includeSignature} onChange={(e) => setIncludeSignature(e.target.checked)} size="small" />} label={<Typography variant="caption" fontWeight="bold">FIRMA</Typography>} sx={{ ml: 1 }} />

        {/* ✅ BOTÓN CARRITO - SIEMPRE VISIBLE */}
        <Tooltip title={`Carrito: ${studiesInCart.length} estudios`}>
            <IconButton 
                color={studiesInCart.length > 0 ? "secondary" : "default"} 
                onClick={() => setOpenCartDialog(true)}
                sx={{ 
                    border: studiesInCart.length > 0 ? '2px solid #9c27b0' : '1px solid #ccc',
                    bgcolor: studiesInCart.length > 0 ? '#f3e5f5' : 'transparent',
                    mr: 1
                }}
            >
                <Badge badgeContent={studiesInCart.length} color="error">
                    <ShoppingCartIcon />
                </Badge>
            </IconButton>
        </Tooltip>

        <Button variant="contained" color="info" onClick={handleArchiveToExpediente} startIcon={isSavingCloud ? <CircularProgress size={20} color="inherit"/> : <CloudUploadIcon />} disabled={isSavingCloud}>
            ARCHIVAR EN EXPEDIENTE
        </Button>

        <Button variant="contained" onClick={handlePrint} startIcon={<PrintIcon />}>IMPRIMIR</Button>
        <Button variant="contained" color="success" onClick={handleSaveRequest} startIcon={<SaveIcon />}>GUARDAR</Button>
        
        <Button variant="outlined" startIcon={<PersonSearchIcon />} onClick={() => setIsPatientSelectionRequired(true)} sx={{ ml: 1 }}>
            Cambiar
        </Button>
      </Box>

      {/* --- CONTENIDO PRINCIPAL --- */}
      <Box sx={{ p: 3, display: 'flex', gap: 3, height: 'calc(100vh - 85px)' }}>
        
        {/* IZQUIERDA: VADEMÉCUM */}
        <Paper sx={{ width: 350, display: 'flex', flexDirection: 'column', p: 2 }}>
            <TextField 
                fullWidth placeholder="Buscar estudio..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon color="action" /> }} sx={{ mb: 2 }} size="small"
            />
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <List dense>
                    {allStudies.map((s) => (
                        <ListItem key={s.id} button divider onClick={() => handleAddStudy(s)}>
                            <ListItemText primary={s.name} secondary={s.description} />
                            <AddIcon color="primary" />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Paper>

        {/* CENTRO: EDITOR DUAL */}
        <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
            {/* --- COLUMNA 1 --- */}
            <Paper sx={{ flex: 1, p: 2, border: activeSide==='left'?'2px solid #1976d2':'1px solid #ddd', display:'flex', flexDirection:'column' }} onClick={()=>setActiveSide('left')}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <Typography variant="subtitle1" color="primary" fontWeight="bold">RECIPE 1 (IZQ)</Typography>
                    <Button 
                        size="small" 
                        variant="contained" 
                        color={leftSide.showData ? "success" : "error"}
                        startIcon={leftSide.showData ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        onClick={(e) => { e.stopPropagation(); setLeftSide(prev => ({...prev, showData: !prev.showData})); }}
                    >
                        {leftSide.showData ? "DATOS VISIBLES" : "DATOS BORRADOS"}
                    </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', opacity: leftSide.showData ? 1 : 0.3 }}>
                    <List dense>
                        {leftSide.studies.map((s, i) => (
                            <ListItem key={s.id + i} secondaryAction={<IconButton size="small" onClick={()=>handleRemoveStudy(s.id)}><DeleteIcon color="error" fontSize="small"/></IconButton>}>
                                <Typography variant="caption" sx={{mr:1, fontWeight:'bold'}}>{i+1}.</Typography>
                                <ListItemText primary={s.name} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <TextField fullWidth multiline rows={2} placeholder="Nota..." value={leftSide.note} onChange={e=>handleNoteChange(e.target.value)} size="small" sx={{mt:1}}/>
            </Paper>

            {/* --- COLUMNA 2 --- */}
            <Paper sx={{ flex: 1, p: 2, border: activeSide==='right'?'2px solid #9c27b0':'1px solid #ddd', display:'flex', flexDirection:'column' }} onClick={()=>setActiveSide('right')}>
                <Box sx={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <Typography variant="subtitle1" color="secondary" fontWeight="bold">RECIPE 2 (DER)</Typography>
                    <Button 
                        size="small" 
                        variant="contained" 
                        color={rightSide.showData ? "success" : "error"}
                        startIcon={rightSide.showData ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        onClick={(e) => { e.stopPropagation(); setRightSide(prev => ({...prev, showData: !prev.showData})); }}
                    >
                        {rightSide.showData ? "DATOS VISIBLES" : "DATOS BORRADOS"}
                    </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ flexGrow: 1, overflowY: 'auto', opacity: rightSide.showData ? 1 : 0.3 }}>
                    <List dense>
                        {rightSide.studies.map((s, i) => (
                            <ListItem key={s.id + i} secondaryAction={<IconButton size="small" onClick={()=>handleRemoveStudy(s.id)}><DeleteIcon color="error" fontSize="small"/></IconButton>}>
                                <Typography variant="caption" sx={{mr:1, fontWeight:'bold'}}>{i+1}.</Typography>
                                <ListItemText primary={s.name} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                <TextField fullWidth multiline rows={2} placeholder="Nota..." value={rightSide.note} onChange={e=>handleNoteChange(e.target.value)} size="small" sx={{mt:1}}/>
            </Paper>
        </Box>
      </Box>

      {/* ✅ DIÁLOGO DEL CARRITO */}
      <Dialog open={openCartDialog} onClose={() => setOpenCartDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9c27b0', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon />
                Carrito de Estudios ({studiesInCart.length})
            </Box>
        </DialogTitle>
        <DialogContent dividers>
            {studiesInCart.length > 0 ? (
                <List dense>
                    {studiesInCart.map((item: any, idx: number) => (
                        <ListItem key={idx} divider>
                            <ListItemText 
                                primary={<Typography fontWeight="bold">{item.name || item.commercialName}</Typography>}
                                secondary={item.description || item.dosage || 'Sin descripción'} 
                            />
                        </ListItem>
                    ))}
                </List>
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ShoppingCartIcon sx={{ fontSize: 60, color: '#ccc' }} />
                    <Typography color="textSecondary" sx={{ mt: 2 }}>
                        El carrito está vacío.
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Agregue estudios desde el catálogo.
                    </Typography>
                </Box>
            )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setOpenCartDialog(false)}>Cerrar</Button>
            <Button 
                variant="outlined" 
                color="error" 
                onClick={() => { clearClipboard(); setSuccess("Carrito vaciado"); }}
                disabled={studiesInCart.length === 0}
            >
                Vaciar Carrito
            </Button>
            <Button 
                variant="contained" 
                color="secondary"
                onClick={handlePasteFromCart}
                disabled={studiesInCart.length === 0}
                startIcon={<AddIcon />}
            >
                Agregar a Columna {activeSide === 'left' ? 'IZQ' : 'DER'}
            </Button>
        </DialogActions>
      </Dialog>

      {/* --- VISTA DE IMPRESIÓN OCULTA --- */}
      {isPrinting && (
        <Box sx={{ position: 'fixed', top: -9999, left: -9999, zIndex: 9999 }}>
            <InternalPrintView 
                patient={selectedPatient} 
                left={leftSide} 
                right={rightSide} 
                config={config} 
                includeSignature={includeSignature}
                user={user}
                processImageUrl={processImageUrl}
            />
        </Box>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={()=>setSuccess('')}><Alert severity="success">{success}</Alert></Snackbar>
      <Snackbar open={!!error} autoHideDuration={3000} onClose={()=>setError('')}><Alert severity="error">{error}</Alert></Snackbar>
    </>
  );
}

// --- COMPONENTE INTERNO DE IMPRESIÓN ---
const InternalPrintView = ({ patient, left, right, config, includeSignature, user, processImageUrl }: any) => {
    const [logoBase64, setLogoBase64] = useState('');
    const [sigBase64, setSigBase64] = useState('');

    useEffect(() => {
        const fetchBase64 = async (url: string) => {
            try {
                const token = localStorage.getItem('token');
                const fullUrl = processImageUrl(url);
                const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(fullUrl)}`;
                const response = await fetch(proxyUrl, { headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) throw new Error();
                const data = await response.json();
                return data.base64;
            } catch {
                return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
            }
        };

        const loadLogo = async () => {
            const rawLogo = config.logoRecipeUrl || config.logoUrl || config.headerImageUrl;
            if (rawLogo) {
                const b64 = await fetchBase64(rawLogo);
                setLogoBase64(b64);
            }
        };

        const loadSig = async () => {
            if (includeSignature && (user?.signature_url || user?.signatureUrl)) {
                const b64 = await fetchBase64(user.signature_url || user.signatureUrl);
                setSigBase64(b64);
            } else {
                setSigBase64(''); 
            }
        };

        loadLogo();
        loadSig();
    }, [config, user, includeSignature, processImageUrl]);

    const calculateAge = (birthDate?: string) => {
        if (!birthDate) return '---';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
        return `${age} años`;
    };

    const Column = ({ data }: any) => {
        return (
            <td style={{ width: 'calc(50% - 7mm)', verticalAlign: 'top', padding: '0 0 0 5px', height: '100%' }}>
                <div style={{ height: '202mm', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    
                    {/* PARTE SUPERIOR */}
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                            {logoBase64 ? <img src={logoBase64} style={{ maxHeight: '50px' }} alt="Logo" /> : <div style={{height:50}}/>}
                        </div>
                        
                        <div style={{ border: '1px solid #000', padding: '4px', marginBottom: '15px', fontSize: '8pt', fontFamily: 'Arial', minHeight: '35px' }}>
                            {data.showData ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ textAlign: 'left' }}><b>NOMBRE:</b> <span style={{ fontSize: '9pt', fontWeight: 'bold' }}>{patient.name.toUpperCase()} {patient.lastName.toUpperCase()}</span></td>
                                            <td style={{ textAlign: 'right', width: '100px' }}><b>EDAD:</b> {calculateAge(patient.birthDate)}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'left', fontSize: '9pt' }}><b>C.I.:</b> {patient.cedula || patient.patientId || '---'}</td>
                                            <td style={{ textAlign: 'right' }}><b>FECHA:</b> {new Date().toLocaleDateString()}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ height: '30px' }}></div>
                            )}
                        </div>

                        {/* LISTA DE ESTUDIOS */}
                        <div style={{ minHeight: '400px', fontSize: '10pt', lineHeight: '1.3', fontFamily: 'Arial' }}>
                            <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '10px', textAlign: 'center', display: data.showData ? 'block' : 'none' }}>SOLICITUD DE ESTUDIOS</div>
                            {data.showData && data.studies.map((s: any, i: number) => (
                                <div key={i} style={{ marginBottom: '8px' }}>
                                    <b>{i + 1}. {s.name}</b>
                                    {s.description && <div style={{ fontSize: '9pt', color: '#333' }}>{s.description}</div>}
                                </div>
                            ))}
                            {data.showData && data.note && <div style={{ marginTop: '15px', fontStyle: 'italic' }}>Nota: {data.note}</div>}
                        </div>
                    </div>

                    {/* PARTE INFERIOR: Firma y Footer */}
                    <div style={{ marginTop: 'auto' }}>
                        <div style={{ minHeight: '80px', marginBottom: '5px' }}>
                            {data.showData && includeSignature && sigBase64 && (
                                <div style={{ textAlign: 'center' }}>
                                    <img src={sigBase64} style={{ maxHeight: '70px', display: 'block', margin: '0 auto' }} alt="Firma" />
                                </div>
                            )}
                            {data.showData && (!includeSignature || !sigBase64) && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <div style={{ height: '40px', borderBottom: '1px solid #000', width: '140px', margin: '0 auto' }}></div>
                                    <small style={{ fontSize: '7pt' }}>SELLO Y FIRMA</small>
                                </div>
                            )}
                        </div>

                        {config.footerAddress && (
                            <div style={{ borderTop: '0.3px solid #ccc', paddingTop: '3px', textAlign: 'center', fontSize: '7pt', color: '#666', fontFamily: 'Arial' }}>
                                {config.footerAddress}
                            </div>
                        )}
                    </div>
                </div>
            </td>
        );
    };

    return (
        <div id="internal-print-view" style={{ 
            width: '279.4mm', height: '215.9mm', backgroundColor: 'white', 
            padding: '6mm', boxSizing: 'border-box' 
        }}>
            <table style={{ width: '100%', height: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <tbody>
                    <tr>
                        <Column data={left} />
                        <td style={{ width: '14mm' }}>&nbsp;</td>
                        <Column data={right} />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default RecipeEstudiosPage;